import { ValidateEarlyAccessRequest, EarlyAccessResponse, EarlyAccessStats } from './types';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3007';

class EarlyAccessService {
  private async makeRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;
    
    const defaultOptions: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    try {
      const response = await fetch(url, defaultOptions);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error(`API request failed for ${endpoint}:`, error);
      throw error;
    }
  }

  async validateEarlyAccess(request: ValidateEarlyAccessRequest): Promise<EarlyAccessResponse> {
    return await this.makeRequest<EarlyAccessResponse>('/early-access/validate', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  async getEarlyAccessStats(): Promise<EarlyAccessStats> {
    return await this.makeRequest<EarlyAccessStats>('/early-access/stats');
  }

  // Utility methods for session management
  setBetaAuthenticated(authenticated: boolean): void {
    if (authenticated) {
      sessionStorage.setItem('beta_authenticated', 'true');
      sessionStorage.setItem('beta_auth_timestamp', new Date().toISOString());
    } else {
      sessionStorage.removeItem('beta_authenticated');
      sessionStorage.removeItem('beta_auth_timestamp');
    }
  }

  isBetaAuthenticated(): boolean {
    const authenticated = sessionStorage.getItem('beta_authenticated') === 'true';
    const timestamp = sessionStorage.getItem('beta_auth_timestamp');
    
    // Check if authentication is still valid (24 hours)
    if (authenticated && timestamp) {
      const authTime = new Date(timestamp);
      const now = new Date();
      const diffHours = (now.getTime() - authTime.getTime()) / (1000 * 60 * 60);
      
      if (diffHours > 24) {
        this.setBetaAuthenticated(false);
        return false;
      }
    }
    
    return authenticated;
  }

  clearBetaAuthentication(): void {
    this.setBetaAuthenticated(false);
  }
}

export const earlyAccessService = new EarlyAccessService();
export default earlyAccessService;
